import {FormActions, FormLayout, SelectField, RichTextField} from '@hconnect/common/components'
import {useUserContext} from '@hconnect/login'
import {spawnErrorToast, spawnSuccessToast, TimeInput} from '@hm/fundamental'
import {yupResolver} from '@hookform/resolvers/yup'
import {Box, Chip, Typography} from '@mui/material'
import {AttachmentsField} from '@widgets/common-attachments'
import {useMemo, useState, useCallback} from 'react'
import {FormProvider, useForm, useFormState, SubmitHandler, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useCreateJobsToBeDoneInsight, useUpdateJobsToBeDoneInsight} from '../hooks'
import {mapJobsToBeDoneInsightFormValuesToCreateDto} from '../mappers'
import {
  JobsToBeDoneTaskWithContext,
  JobsToBeDoneInsightCreateDto,
  JobsToBeDoneInsightFormValues,
  JobsToBeDoneInsightDetails,
  AttachmentDto
} from '../types'
import {getJobsToBeDoneInsightFormValidationSchema} from '../validators'

type Props = {
  onCancel: () => void
  plantUpmId: string
  task: JobsToBeDoneTaskWithContext
  existingInsight?: JobsToBeDoneInsightDetails
}

type ExistingAttachmentItemProps = {
  attachment: AttachmentDto
  onRemove: (id: string) => void
}

const ExistingAttachmentItem = ({attachment, onRemove}: ExistingAttachmentItemProps) => {
  return (
    <Chip
      label={attachment.fileName}
      onDelete={() => onRemove(attachment.id)}
      variant="outlined"
      size="small"
      sx={{
        maxWidth: '200px',
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      }}
      data-test-id={`existing-attachment-${attachment.id}`}
    />
  )
}

export const JtbdInsightForm = ({task, plantUpmId, onCancel, existingInsight}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {loggedUser} = useUserContext()
  const isEditMode = !!existingInsight

  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([])

  // Compute remaining existing attachments (not yet removed)
  const remainingExistingAttachments = useMemo(() => {
    if (!existingInsight?.attachments) return []

    return existingInsight.attachments.filter((a) => !removedAttachmentIds.includes(a.id))
  }, [existingInsight?.attachments, removedAttachmentIds])

  const handleRemoveExistingAttachment = useCallback((attachmentId: string) => {
    setRemovedAttachmentIds((prev) => [...prev, attachmentId])
  }, [])

  const validationSchema = useMemo(() => getJobsToBeDoneInsightFormValidationSchema({t}), [t])

  const defaultValues = useMemo(
    () => ({
      id: existingInsight?.id || '',
      authorId: existingInsight?.createdBy.id || loggedUser?.user_id || '',
      jobToBeDoneId: task.jobToBeDoneId,
      activityId: task.activityId,
      activityTaskId: task.id,
      plantUpmId,
      description: existingInsight?.description || '',
      attachments: [], // New attachments only - existing are tracked separately,
      timeSpent: existingInsight?.timeSpent || ''
    }),
    [existingInsight, loggedUser?.user_id, task.jobToBeDoneId, task.activityId, task.id, plantUpmId]
  )

  const methods = useForm<JobsToBeDoneInsightFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    values: defaultValues
  })

  const {isValid, isSubmitting} = useFormState({
    control: methods.control
  })

  const handleCreateSuccess = () => {
    spawnSuccessToast(t('jobsToBeDone.message.insightAdded'))
  }
  const handleCreateError = () => {
    spawnErrorToast(t('jobsToBeDone.message.failedToAddInsight'))
  }

  const {mutateAsync: createJobsToBeDoneInsight} = useCreateJobsToBeDoneInsight(
    {
      plantUpmId,
      jobToBeDoneId: task.jobToBeDoneId,
      activityId: task.activityId,
      activityTaskId: task.id
    },
    {
      onSuccess: handleCreateSuccess,
      onError: handleCreateError
    }
  )

  const handleUpdateSuccess = () => {
    spawnSuccessToast(t('jobsToBeDone.message.insightUpdated'))
    onCancel()
  }
  const handleUpdateError = () => {
    spawnErrorToast(t('jobsToBeDone.message.failedToUpdateInsight'))
  }

  const {mutateAsync: updateJobsToBeDoneInsight} = useUpdateJobsToBeDoneInsight({
    plantUpmId,
    jobToBeDoneId: task.jobToBeDoneId,
    activityId: task.activityId,
    activityTaskId: task.id,
    insightId: existingInsight?.id || ''
  })

  const handleSubmit: SubmitHandler<JobsToBeDoneInsightFormValues> = async (formData) => {
    if (isEditMode) {
      await updateJobsToBeDoneInsight(
        {
          timeSpent: formData.timeSpent,
          description: formData.description,
          newAttachments: formData.attachments,
          attachmentIdsToRemove: removedAttachmentIds
        },
        {
          onSuccess: handleUpdateSuccess,
          onError: handleUpdateError
        }
      )
    } else {
      const dataDto: JobsToBeDoneInsightCreateDto =
        mapJobsToBeDoneInsightFormValuesToCreateDto(formData)
      await createJobsToBeDoneInsight(dataDto, {
        onSuccess: () => {
          onCancel()
        }
      })
    }
  }

  const authorName = isEditMode ? existingInsight.createdBy.name : loggedUser?.name || ''
  const authorId = isEditMode ? existingInsight.createdBy.id : loggedUser?.user_id || ''

  // Total attachments count (remaining existing + new)
  const newAttachments = methods.watch('attachments') || []
  const totalAttachmentsCount = remainingExistingAttachments.length + newAttachments.length

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormLayout>
          <SelectField
            label={t('jobsToBeDone.form.author')}
            options={[{label: authorName, value: authorId}]}
            disabled
            name="authorId"
            control={methods.control}
          />
          <SelectField
            label={t('jobsToBeDone.form.jtbd')}
            options={[{label: task.jobsToBeDoneTitle, value: task.jobToBeDoneId}]}
            disabled
            value={task.jobToBeDoneId}
            name="jobToBeDoneId"
            control={methods.control}
          />
          <SelectField
            label={t('jobsToBeDone.form.activity')}
            options={[{label: task.activityTitle, value: task.activityId}]}
            disabled
            name="activityId"
            control={methods.control}
          />
          <SelectField
            label={t('jobsToBeDone.form.task')}
            options={[{label: task.title, value: task.id}]}
            disabled
            name="activityTaskId"
            control={methods.control}
          />
          <Controller
            name="timeSpent"
            control={methods.control}
            render={({field}) => (
              <TimeInput
                data-cy="time-spent-input"
                label={t('jobsToBeDone.form.timeSpent')}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                maxHours={100}
              />
            )}
          />
          <Controller
            name="description"
            control={methods.control}
            render={({field, fieldState}) => (
              <RichTextField
                data-test-id="rich-text-description"
                current={field.value}
                onChange={field.onChange}
                disabled={false}
                label={t('jobsToBeDone.form.description')}
                errorCodeLabel={fieldState.error?.message}
              />
            )}
          />

          {/* Attachments section */}
          <Box sx={{mt: 2}}>
            <Typography variant="subtitle2" sx={{mb: 1}}>
              {t('jobsToBeDone.form.attachments')} ({totalAttachmentsCount})
            </Typography>

            {/* Existing attachments (edit mode only) */}
            {remainingExistingAttachments.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2
                }}
              >
                {remainingExistingAttachments.map((attachment) => (
                  <ExistingAttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    onRemove={handleRemoveExistingAttachment}
                  />
                ))}
              </Box>
            )}

            {/* New attachments dropzone */}
            <AttachmentsField
              translationPrefix="jobsToBeDone.form"
              name="attachments"
              control={methods.control}
            />
          </Box>

          <FormActions onCancel={onCancel} isSubmitting={isSubmitting} isValid={isValid} />
        </FormLayout>
      </form>
    </FormProvider>
  )
}
